import type { PolicyDriver } from "./schema";

/**
 * CSR-1345 — masked vs unmasked driver field reconciliation.
 *
 * Progressive scrapes deliver masked values (license "*****8491", DOB
 * "**\/**\/1982") while the NowCerts/handoff push delivers the real ones
 * ("051638491" / "1982-02-27"). Both shapes describe the same physical driver,
 * and historically they coexisted as separate roster entries — or worse, a
 * masked sync overwrote previously-known real values.
 *
 * This module is shared (not server-only) because both the server-side merge
 * (`server/asset-merge.ts`) and the client-side display aggregation
 * (`client/src/components/insured/assets/asset-helpers.ts`) need the same
 * convergence and precedence rules.
 */

/** A non-empty value containing a Progressive mask character. */
export function isMaskedDriverValue(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim() !== "" && value.includes("*");
}

/** A non-empty value with NO mask characters — real, display-worthy data. */
export function hasRealDriverValue(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim() !== "" && !value.includes("*");
}

/**
 * Trailing unmasked run of a masked license ("*****8491" -> "8491").
 * Returns null when the value is not masked or has no usable suffix.
 */
export function maskedLicenseSuffix(value: string): string | null {
  const match = value.trim().match(/\*([A-Za-z0-9]+)$/);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Tri-state evidence comparison. "match" = positive evidence the two values
 * describe the same datum; "conflict" = positive evidence they do not;
 * "neutral" = no usable evidence (one side empty/unparseable).
 */
export type FieldAffinity = "match" | "neutral" | "conflict";

function normalizeLicense(value: string): string {
  return value.trim().toUpperCase();
}

/**
 * Compare two license numbers across mask boundaries.
 * - both real: normalized equality
 * - masked vs real: the masked suffix must terminate the real value
 *   ("*****8491" <-> "051638491")
 * - both masked: suffix equality ("*****8491" <-> "*********8491")
 * - either side empty/suffix-less: neutral
 */
export function licenseAffinity(
  a: string | null | undefined,
  b: string | null | undefined,
): FieldAffinity {
  const av = typeof a === "string" ? a.trim() : "";
  const bv = typeof b === "string" ? b.trim() : "";
  if (!av || !bv) return "neutral";

  const aMasked = av.includes("*");
  const bMasked = bv.includes("*");

  if (!aMasked && !bMasked) {
    return normalizeLicense(av) === normalizeLicense(bv) ? "match" : "conflict";
  }
  if (aMasked && bMasked) {
    const aSuffix = maskedLicenseSuffix(av);
    const bSuffix = maskedLicenseSuffix(bv);
    if (!aSuffix || !bSuffix) return "neutral";
    return aSuffix === bSuffix ? "match" : "conflict";
  }
  const suffix = maskedLicenseSuffix(aMasked ? av : bv);
  if (!suffix || suffix.length < 2) return "neutral";
  const real = normalizeLicense(aMasked ? bv : av);
  return real.endsWith(suffix) ? "match" : "conflict";
}

const ISO_DATE = /^(\d{4})-(\d{1,2})-(\d{1,2})/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

function dobParts(value: string): { y: number; m: number; d: number } | null {
  const iso = value.match(ISO_DATE);
  if (iso) return { y: Number(iso[1]), m: Number(iso[2]), d: Number(iso[3]) };
  const us = value.match(US_DATE);
  if (us) return { y: Number(us[3]), m: Number(us[1]), d: Number(us[2]) };
  return null;
}

/** First plausible 4-digit year in a DOB string ("**\/**\/1982" -> "1982"). */
export function dobYear(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const match = value.match(/(?:19|20)\d{2}/);
  return match ? match[0] : null;
}

/**
 * Compare two DOBs across mask boundaries.
 * - both fully parseable real dates: same year+month+day
 * - masked vs real (or unparseable formats): year equality
 * - either side empty / no parseable year: neutral
 */
export function dobAffinity(
  a: string | null | undefined,
  b: string | null | undefined,
): FieldAffinity {
  const av = typeof a === "string" ? a.trim() : "";
  const bv = typeof b === "string" ? b.trim() : "";
  if (!av || !bv) return "neutral";

  const ay = dobYear(av);
  const by = dobYear(bv);
  if (!ay || !by) return "neutral";
  if (ay !== by) return "conflict";

  const ap = dobParts(av);
  const bp = dobParts(bv);
  if (ap && bp) {
    return ap.m === bp.m && ap.d === bp.d ? "match" : "conflict";
  }
  return "match";
}

function effectiveDob(d: PolicyDriver): string | undefined {
  return d.dateOfBirth ?? d.birthday;
}

function hasStableId(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim() !== "";
}

/**
 * Whether two driver rows are variants of the same physical driver.
 *
 * - equal databaseId -> match; differing databaseIds -> conflict (two
 *   legitimate same-name drivers, CSR-1201 D2)
 * - license/DOB evidence combined: any conflict wins, except a license
 *   conflict corroborated by a full real-DOB match (license re-issue), which
 *   downgrades to neutral
 * - name similarity is deliberately NOT considered here — name-only dedup is
 *   the caller's decision and is forbidden when this returns "conflict"
 */
export function driverVariantAffinity(a: PolicyDriver, b: PolicyDriver): FieldAffinity {
  if (hasStableId(a.databaseId) && hasStableId(b.databaseId)) {
    return a.databaseId!.trim().toLowerCase() === b.databaseId!.trim().toLowerCase()
      ? "match"
      : "conflict";
  }

  const license = licenseAffinity(a.licenseNumber, b.licenseNumber);
  const dob = dobAffinity(effectiveDob(a), effectiveDob(b));

  if (dob === "conflict") return "conflict";
  if (license === "conflict") return dob === "match" ? "neutral" : "conflict";
  if (license === "match" || dob === "match") return "match";
  return "neutral";
}

function isMissing(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === "string" && value.trim() === "");
}

/**
 * Field-level precedence merge: `primary`'s values win except where they are
 * masked or empty and `fallback` carries a real, non-conflicting value.
 *
 * Invariant (CSR-1345 DoD 2): a real value (DOB "1990-06-23", license
 * "051638491") is NEVER overwritten by a masked one ("**\/**\/1990",
 * "*****8491") or by an empty string. A real primary value always wins —
 * and a masked primary value that positively CONFLICTS with the fallback
 * (e.g. a changed license) is kept, since it is fresher evidence.
 */
export function mergeDriverVariantFields<T extends PolicyDriver>(
  primary: T,
  fallback: PolicyDriver,
): T {
  const out: T = { ...primary };

  if (isMissing(out.databaseId) && !isMissing(fallback.databaseId)) {
    out.databaseId = fallback.databaseId;
  }
  for (const field of ["name", "firstName", "lastName", "licenseState"] as const) {
    if (isMissing(out[field]) && !isMissing(fallback[field])) {
      out[field] = fallback[field];
    }
  }

  // Precedence ladder per field: empty < masked < real. An empty primary takes
  // any non-empty fallback; a masked primary upgrades to a real fallback unless
  // the two positively conflict (changed license / different birth date).
  const fallbackLicense = fallback.licenseNumber;
  if (!isMissing(fallbackLicense)) {
    if (isMissing(out.licenseNumber)) {
      out.licenseNumber = fallbackLicense;
    } else if (
      isMaskedDriverValue(out.licenseNumber) &&
      hasRealDriverValue(fallbackLicense) &&
      licenseAffinity(out.licenseNumber, fallbackLicense) !== "conflict"
    ) {
      out.licenseNumber = fallbackLicense;
    }
  }

  const fallbackDob = effectiveDob(fallback);
  if (!isMissing(fallbackDob)) {
    if (isMissing(out.dateOfBirth)) {
      out.dateOfBirth = fallbackDob;
    } else if (
      isMaskedDriverValue(out.dateOfBirth) &&
      hasRealDriverValue(fallbackDob) &&
      dobAffinity(out.dateOfBirth, fallbackDob) !== "conflict"
    ) {
      out.dateOfBirth = fallbackDob;
    }
  }

  return out;
}
